<template>
  <div class="row">
    <div class="col-12 text-center">
      {{ this.getDateFormatted() }}
    </div>
    <nav aria-label="Page navigation example">
      <ul class="pagination justify-content-between">
        <li class="page-item">
          <a class="page-link" @click="loadPrev()">&laquo;</a>
        </li>
        <li class="page-item">
          <a class="page-link" href="#" @click="loadNext()">&raquo;</a>
        </li>
      </ul>
    </nav>
  </div>
  <div class="row">
    <div class="col-12 my-2" v-for="(data, orga) in sortedPlantsByOrganisation" :key="orga">
      <div class="card shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <p class="h5 mb-0">
            {{ orga }}
            <span class="badge bg-secondary ms-2" v-if="data.push_billing_type === 'flat'"
              >Push: {{ $t('push_billing_flat') }}</span
            >
            <span class="badge bg-secondary ms-2" v-else-if="data.push_billing_type === 'per_plant'"
              >Push: {{ $t('push_billing_per_plant') }}</span
            >
          </p>

          <!-- Dropdown Menü -->
          <div class="dropdown">
            <button
              class="btn"
              type="button"
              id="dropdownMenuButton"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i class="fa-solid fa-ellipsis-vertical"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuButton">
              <li>
                <a
                  class="dropdown-item"
                  href="#"
                  @click="downloadReport(data.organisation_id, orga)"
                >
                  <i class="fa-solid fa-file-pdf"></i> {{ $t('export_itemization_pdf') }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div class="card-body">
          <div
            v-if="data.invoice_recipient && data.invoice_recipient.name"
            class="border rounded p-2 mt-1 bg-light small text-muted my-2"
          >
            <div class="row">
              <!-- Linke Spalte: Name + Adresse -->
              <div class="col">
                <div>
                  <strong>{{ data.invoice_recipient.name }}</strong>
                </div>
                <div v-if="data.invoice_recipient.zusatz">
                  📧 {{ data.invoice_recipient.zusatz }}
                </div>
                <div>{{ data.invoice_recipient.strasse }}</div>
                <div>{{ data.invoice_recipient.plz }} {{ data.invoice_recipient.ort }}</div>
                <div>{{ data.invoice_recipient.land }}</div>
              </div>

              <!-- Rechte Spalte: Kontakt + Steuer -->
              <div class="col">
                <div v-if="data.invoice_recipient.email">📧 {{ data.invoice_recipient.email }}</div>
                <div v-if="data.invoice_recipient.telefon">
                  📞 {{ data.invoice_recipient.telefon }}
                </div>
                <div v-if="data.invoice_recipient.ust_id">
                  USt-ID: {{ data.invoice_recipient.ust_id }}
                </div>
              </div>
            </div>
          </div>
          <!-- Desktop table -->
          <div v-if="!isMobile" class="table-responsive">
            <table class="table table-striped table-hover table-bordered">
              <thead>
                <tr>
                  <th class="w-50" rowspan="2">{{ $t('park_plant') }}</th>
                  <th class="w-25" rowspan="2">{{ $t('cost') }}</th>
                  <th rowspan="2">{{ $t('is_invoiced') }}</th>
                  <th rowspan="2">{{ $t('is_payed') }}</th>
                  <th class="w-12" colspan="2">{{ $t('num_sms') }}</th>
                  <th class="w-12" colspan="2">{{ $t('num_email') }}</th>
                  <th colspan="2">Push</th>
                </tr>
                <tr>
                  <th>{{ $t('quantity') }}</th>
                  <th>{{ $t('cost') }}</th>
                  <th>{{ $t('quantity') }}</th>
                  <th>{{ $t('cost') }}</th>
                  <th>{{ $t('push_active') }}</th>
                  <th>{{ $t('cost') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="plant in data.plants" v-bind:key="plant.name">
                  <td>
                    {{ plant.name }}
                    <i class="fa-solid fa-eye" v-if="plant.is_view_plant"></i>
                    <i class="fa-solid fa-bench-tree" v-if="plant.is_park"></i>
                    <div
                      v-if="plant.invoice_recipient"
                      class="border rounded p-2 mt-1 bg-light small text-muted"
                    >
                      <div class="row">
                        <!-- Linke Spalte: Name + Adresse -->
                        <div class="col">
                          <div>
                            <strong>{{ plant.invoice_recipient.name }}</strong>
                          </div>
                          <div v-if="plant.invoice_recipient.zusatz">
                            📧 {{ plant.invoice_recipient.zusatz }}
                          </div>
                          <div>{{ plant.invoice_recipient.strasse }}</div>
                          <div>
                            {{ plant.invoice_recipient.plz }} {{ plant.invoice_recipient.ort }}
                          </div>
                          <div>{{ plant.invoice_recipient.land }}</div>
                        </div>

                        <!-- Rechte Spalte: Kontakt + Steuer -->
                        <div class="col">
                          <div v-if="plant.invoice_recipient.email">
                            📧 {{ plant.invoice_recipient.email }}
                          </div>
                          <div v-if="plant.invoice_recipient.telefon">
                            📞 {{ plant.invoice_recipient.telefon }}
                          </div>
                          <div v-if="plant.invoice_recipient.ust_id">
                            USt-ID: {{ plant.invoice_recipient.ust_id }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control"
                      :disabled="isMonthlyInputDisabled(plant)"
                      v-model="plant.monthly_cost"
                      @input="debouncedUpdate(plant, data.organisation_id, 'monthly_cost')"
                    />
                    <small
                      v-if="plant.billing_type === 'yearly' && plant.yearly_billing_month"
                      class="text-muted"
                    >
                      Jährliche Abrechnung ({{ getMonthName(plant.yearly_billing_month) }})
                    </small>
                  </td>
                  <td>
                    <div class="form-check form-switch">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        role="switch"
                        v-model="plant.is_invoiced"
                        @change="
                          updateBillingInformation(plant, data.organisation_id, 'is_invoiced')
                        "
                      />
                    </div>
                  </td>
                  <td>
                    <div class="form-check form-switch">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        role="switch"
                        v-model="plant.is_payed"
                        @change="updateBillingInformation(plant, data.organisation_id, 'is_payed')"
                      />
                    </div>
                  </td>
                  <td>{{ plant.total_sms }}</td>
                  <td>{{ plant.price_sms }} €</td>
                  <td>{{ plant.total_email }}</td>
                  <td>{{ plant.price_email }} €</td>
                  <td class="text-center">
                    <i class="fa-solid fa-bell text-success" v-if="plant.push_is_enabled"></i>
                    <i class="fa-regular fa-bell text-muted" v-else></i>
                  </td>
                  <td>
                    <template v-if="data.push_billing_type === 'per_plant'"
                      >{{ plant.push_price }} €</template
                    >
                    <template v-else-if="data.push_billing_type === 'flat'">—</template>
                    <template v-else>—</template>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th>Summe</th>
                  <th>{{ data.sum_monthly_cost }} €</th>
                  <th></th>
                  <th></th>
                  <th>{{ data.sum_sms }}</th>
                  <th>{{ data.sum_sms_price }} €</th>
                  <th>{{ data.sum_email }}</th>
                  <th>{{ data.sum_email_price }} €</th>
                  <th></th>
                  <th v-if="data.push_billing_type === 'per_plant'">{{ data.sum_push_price }} €</th>
                  <th v-else-if="data.push_billing_type === 'flat'">
                    {{ data.push_flat_price }} €
                    <small class="text-muted">({{ $t('push_billing_flat_hint') }})</small>
                  </th>
                  <th v-else>—</th>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Mobile: Apple-style grouped list -->
          <div v-else class="mt-1">
            <div
              v-for="plant in data.plants"
              :key="plant.name + '-mobile'"
              class="ios-plant-card mb-3"
            >
              <div
                v-if="plant.invoice_recipient && plant.invoice_recipient.name"
                class="ios-recipient"
              >
                <div class="ios-recipient__name">{{ plant.invoice_recipient.name }}</div>
                <div v-if="plant.invoice_recipient.strasse" class="ios-recipient__detail">
                  {{ plant.invoice_recipient.strasse }}, {{ plant.invoice_recipient.plz }}
                  {{ plant.invoice_recipient.ort }}
                </div>
                <div v-if="plant.invoice_recipient.email" class="ios-recipient__detail">
                  📧 {{ plant.invoice_recipient.email }}
                </div>
              </div>

              <div class="ios-row ios-row--header">
                <span class="ios-row__title">{{ plant.name }}</span>
                <span class="ios-row__icons">
                  <i class="fa-solid fa-eye" v-if="plant.is_view_plant"></i>
                  <i class="fa-solid fa-bench-tree" v-if="plant.is_park"></i>
                </span>
              </div>

              <div class="ios-row">
                <span class="ios-row__label">Monatliche Kosten</span>
                <input
                  type="number"
                  class="ios-row__input"
                  :disabled="isMonthlyInputDisabled(plant)"
                  v-model="plant.monthly_cost"
                  @input="debouncedUpdate(plant, data.organisation_id, 'monthly_cost')"
                />
              </div>

              <div
                v-if="plant.billing_type === 'yearly' && plant.yearly_billing_month"
                class="ios-row ios-row--hint"
              >
                <span>Jährliche Abrechnung</span>
                <span class="ios-row__secondary">{{
                  getMonthName(plant.yearly_billing_month)
                }}</span>
              </div>

              <div class="ios-row">
                <span class="ios-row__label">Fakturiert</span>
                <div class="form-check form-switch mb-0">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    role="switch"
                    v-model="plant.is_invoiced"
                    @change="updateBillingInformation(plant, data.organisation_id, 'is_invoiced')"
                  />
                </div>
              </div>

              <div class="ios-row ios-row--last">
                <span class="ios-row__label">Bezahlt</span>
                <div class="form-check form-switch mb-0">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    role="switch"
                    v-model="plant.is_payed"
                    @change="updateBillingInformation(plant, data.organisation_id, 'is_payed')"
                  />
                </div>
              </div>

              <div class="ios-stats">
                <div class="ios-stats__item">
                  <span class="ios-stats__label">SMS</span>
                  <span class="ios-stats__count">{{ plant.total_sms }}</span>
                  <span class="ios-stats__price">{{ plant.price_sms }} €</span>
                </div>
                <div class="ios-stats__divider"></div>
                <div class="ios-stats__item">
                  <span class="ios-stats__label">E-Mail</span>
                  <span class="ios-stats__count">{{ plant.total_email }}</span>
                  <span class="ios-stats__price">{{ plant.price_email }} €</span>
                </div>
                <template v-if="data.push_billing_type !== 'free'">
                  <div class="ios-stats__divider"></div>
                  <div class="ios-stats__item">
                    <span class="ios-stats__label">Push</span>
                    <span class="ios-stats__count">
                      <i class="fa-solid fa-bell text-success" v-if="plant.push_is_enabled"></i>
                      <i class="fa-regular fa-bell text-muted" v-else></i>
                    </span>
                    <span class="ios-stats__price" v-if="data.push_billing_type === 'per_plant'"
                      >{{ plant.push_price }} €</span
                    >
                    <span class="ios-stats__price" v-else>—</span>
                  </div>
                </template>
              </div>
            </div>

            <div class="ios-org-total">
              <div class="ios-org-total__item">
                <span>Monatliche Kosten</span>
                <span class="fw-semibold">{{ data.sum_monthly_cost }} €</span>
              </div>
              <div class="ios-org-total__item">
                <span>SMS</span>
                <span>{{ data.sum_sms }} Stk. · {{ data.sum_sms_price }} €</span>
              </div>
              <div class="ios-org-total__item">
                <span>E-Mail</span>
                <span>{{ data.sum_email }} Stk. · {{ data.sum_email_price }} €</span>
              </div>
              <div class="ios-org-total__item" v-if="data.push_billing_type === 'per_plant'">
                <span>Push</span>
                <span>{{ data.sum_push_price }} €</span>
              </div>
              <div class="ios-org-total__item" v-else-if="data.push_billing_type === 'flat'">
                <span>Push ({{ $t('push_billing_flat_hint') }})</span>
                <span>{{ data.push_flat_price }} €</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Sticky Card -->
  <div
    class="card shadow-lg border-0"
    :style="{
      position: 'fixed',
      bottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom, 0px) + 0.5rem)' : '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '400px',
      zIndex: 1050,
    }"
  >
    <div class="card-body d-flex justify-content-between text-center">
      <div>
        <strong>Gesamt:</strong><br />
        {{ globalSums.total }} €
      </div>
      <div>
        <strong>Bezahlt:</strong><br />
        {{ globalSums.paid }} €
      </div>
      <div>
        <strong>Offen:</strong><br />
        {{ globalSums.open }} €
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const fileDownload = require('js-file-download');

export default {
  name: 'PlantNotifications',
  components: {},
  data() {
    return {
      notification_parks: [],
      currentDate: {},
      liveDate: {},
      debounceTimers: {},
      isMobile: false,
    };
  },
  mounted() {
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', this.onResize);
    this.getDataForThisMonth();
  },
  updated() {},
  unmounted() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth < 768;
    },
    updateBillingInformation(plant, organisation_id, field) {
      axios
        .patch(
          `/api/v1/billing/update_billing_information/organisations/${organisation_id}/item/${plant.id}/`,
          {
            [field]: plant[field],
            ['month']: this.currentDate.getMonth() + 1,
            ['year']: this.currentDate.getFullYear(),
            ['is_park']: plant.is_park,
          }
        )
        .then(() => {
          this.getDataForThisMonth();
        })
        .catch((err) => {
          console.error('Update failed:', err);
        });
    },

    // mit Debounce (für monthly_cost)
    debouncedUpdate(plant, organisation_id, field) {
      const key = `${plant.id}-${field}`;

      if (this.debounceTimers[key]) {
        clearTimeout(this.debounceTimers[key]);
      }

      this.debounceTimers[key] = setTimeout(() => {
        this.updateBillingInformation(plant, organisation_id, field);
      }, 500); // 0,5 Sekunden warten
    },
    getDataForThisMonth() {
      console.log('entry getDataForThisMonth');
      if (!this.currentDate || !(this.currentDate instanceof Date)) {
        this.currentDate = new Date();
        this.liveDate = new Date();
      }
      var lastDayOfMonth = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        0
      );
      let startIntervall = {};
      let endIntervall = {};
      startIntervall['day'] = '1';
      startIntervall['month'] = lastDayOfMonth.getMonth();
      startIntervall['year'] = lastDayOfMonth.getFullYear();
      endIntervall['day'] = lastDayOfMonth.getDate();
      endIntervall['month'] = lastDayOfMonth.getMonth();
      endIntervall['year'] = lastDayOfMonth.getFullYear();
      var formData = {};
      formData.startIntervall = startIntervall;
      formData.endIntervall = endIntervall;
      formData.type_data = 'plain';
      axios
        .post('/api/v1/organisation/get_plant_notification_park/', formData)
        .then((response) => {
          this.notification_parks = response.data;
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
          this.notification_parks = [];
        });
      console.log('exit getDataForThisMonth');
    },
    loadNext() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1
      );
      this.getDataForThisMonth();
    },
    loadPrev() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1
      );
      this.getDataForThisMonth();
    },

    getDateFormatted() {
      if (!this.currentDate || !(this.currentDate instanceof Date)) {
        return '';
      }
      return this.currentDate.toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' });
    },
    downloadReport(organisation_id, organisation_name) {
      console.log('entry downloadReport', organisation_id);
      axios
        .get(
          `/api/v1/organisation/${organisation_id}/notification_report/${this.currentDate.getFullYear()}/${this.currentDate.getMonth() + 1}`,
          { responseType: 'blob' }
        )
        .then((response) => {
          console.log('fertig');
          fileDownload(
            response.data,
            `${this.currentDate.getFullYear()}_${this.currentDate.getMonth() + 1}_Einzelnachweise_${organisation_name}.pdf`
          );
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
    },
    isMonthlyInputDisabled(plant) {
      // Prüfe ob wir nicht im aktuellen Monat sind
      if (this.currentDate.getMonth() !== this.liveDate.getMonth()) {
        return true;
      }

      // Prüfe ob es eine jährliche Abrechnung ist
      if (plant.billing_type === 'yearly' && plant.yearly_billing_month) {
        // Erlaube Eingabe nur im konfigurierten Abrechnungsmonat (Monate sind 0-basiert in JS, aber 1-basiert im Backend)
        const currentMonthBackend = this.currentDate.getMonth() + 1;
        return currentMonthBackend !== plant.yearly_billing_month;
      }

      // Bei monatlicher Abrechnung oder mixed: immer erlauben (wenn aktueller Monat)
      return false;
    },
    getMonthName(monthNumber) {
      const months = [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Oktober',
        'November',
        'Dezember',
      ];
      return months[monthNumber - 1] || monthNumber;
    },
  },
  computed: {
    globalSums() {
      let total = 0;
      let paid = 0;

      Object.values(this.sortedPlantsByOrganisation).forEach((org) => {
        org.plants.forEach((plant) => {
          const cost = parseFloat(plant.monthly_cost) || 0;
          total += cost;
          if (plant.is_payed) {
            paid += cost;
          }
        });
      });

      return {
        total: total.toFixed(2),
        paid: paid.toFixed(2),
        open: (total - paid).toFixed(2),
      };
    },
    currentProperties() {
      return { allOrganisations: this.allOrganisations };
    },
    currentNotificationProperties() {
      return { organisation: this.currentOrganisation };
    },
    sortedPlantsByOrganisation() {
      // data ist ein Objekt mit Organisationsnamen als Keys
      const result = {};

      Object.entries(this.notification_parks).forEach(([orgName, orgData]) => {
        const allPlants = orgData.plants || [];

        // Ansichtanlagen herausfiltern
        const plants = allPlants.filter((p) => !p.is_view_plant);

        // Orgas ohne verbleibende Anlagen überspringen
        if (plants.length === 0) return;

        // Split plants in zwei Gruppen
        const noRecipient = plants.filter((p) => {
          // Prüfe ob invoice_recipient leer ist
          return (
            !p.invoice_recipient ||
            !p.invoice_recipient.name ||
            p.invoice_recipient.name.trim() === ''
          );
        });
        const withRecipient = plants.filter((p) => {
          return (
            p.invoice_recipient &&
            p.invoice_recipient.name &&
            p.invoice_recipient.name.trim() !== ''
          );
        });

        // Sortiere noRecipient alphabetisch nach Anlagenname
        noRecipient.sort((a, b) => a.name.localeCompare(b.name));

        // Sortiere withRecipient zuerst nach Rechnungsempfängername, dann nach Anlagenname
        withRecipient.sort((a, b) => {
          const cmpRecipient = a.invoice_recipient.name.localeCompare(b.invoice_recipient.name);
          if (cmpRecipient !== 0) return cmpRecipient;
          return a.name.localeCompare(b.name);
        });

        // Kombiniere Ergebnis: erst ohne Rechnungsempfänger, dann mit
        result[orgName] = {
          ...orgData,
          plants: [...noRecipient, ...withRecipient],
        };
      });

      return result;
    },
  },
};
</script>

<style scoped>
@media (max-width: 767.98px) {
  .billing-summary-card {
    bottom: calc(64px + env(safe-area-inset-bottom, 0px) + 0.5rem);
  }

  .ios-plant-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 0 0 0.5px rgba(0, 0, 0, 0.06);
  }

  .ios-recipient {
    padding: 10px 16px;
    border-bottom: 0.5px solid rgba(60, 60, 67, 0.18);
    font-size: 0.8125rem;
    color: #6c757d;
    background: #f9f9fb;
  }

  .ios-recipient__name {
    font-weight: 600;
    color: #1c1c1e;
    margin-bottom: 2px;
  }

  .ios-recipient__detail {
    line-height: 1.5;
  }

  .ios-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 11px 16px;
    min-height: 44px;
    background: #ffffff;
    border-bottom: 0.5px solid rgba(60, 60, 67, 0.18);
  }

  .ios-row--header {
    background: #f2f2f7;
    font-weight: 600;
    font-size: 0.9375rem;
  }

  .ios-row--hint {
    background: #f9f9fb;
    min-height: 36px;
    font-size: 0.8125rem;
    color: #8e8e93;
  }

  .ios-row--last {
    border-bottom: none;
  }

  .ios-row__label,
  .ios-row__title {
    font-size: 0.9375rem;
    color: #1c1c1e;
  }

  .ios-row__secondary {
    font-size: 0.8125rem;
    color: #8e8e93;
  }

  .ios-row__icons {
    display: flex;
    gap: 6px;
    font-size: 0.875rem;
    color: #8e8e93;
  }

  .ios-row__input {
    border: none;
    text-align: right;
    font-size: 0.9375rem;
    color: #007aff;
    width: 90px;
    background: transparent;
    padding: 0;
    -moz-appearance: textfield;
    appearance: textfield;
  }

  .ios-row__input::-webkit-outer-spin-button,
  .ios-row__input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  .ios-row__input:disabled {
    color: #8e8e93;
  }

  .ios-row__input:focus {
    outline: none;
  }

  .ios-stats {
    display: flex;
    background: #f9f9fb;
    border-top: 0.5px solid rgba(60, 60, 67, 0.18);
  }

  .ios-stats__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 8px;
    gap: 2px;
  }

  .ios-stats__label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8e8e93;
  }

  .ios-stats__count {
    font-size: 1rem;
    font-weight: 600;
    color: #1c1c1e;
  }

  .ios-stats__price {
    font-size: 0.8125rem;
    color: #8e8e93;
  }

  .ios-stats__divider {
    width: 0.5px;
    background: rgba(60, 60, 67, 0.18);
    align-self: stretch;
    margin: 8px 0;
  }

  .ios-org-total {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 0 0 0.5px rgba(0, 0, 0, 0.06);
    margin-bottom: 1rem;
  }

  .ios-org-total__item {
    display: flex;
    justify-content: space-between;
    padding: 11px 16px;
    font-size: 0.875rem;
    border-bottom: 0.5px solid rgba(60, 60, 67, 0.12);
    color: #6c757d;
  }

  .ios-org-total__item:last-child {
    border-bottom: none;
  }
}
</style>
